/**
 * Allowlist-based HTML sanitizer for rendering server-supplied rich text
 * (notification / support-ticket bodies, announcements) with `v-html`.
 *
 * The app has no DOMPurify dependency, so this parses the input with the
 * browser `DOMParser` (which is inert — embedded <script> never executes) and
 * rebuilds the tree keeping only an allowlist of formatting tags/attributes.
 * Everything else — <script>/<style>, `on*` handlers, `style`/`class`,
 * `javascript:`/`data:` URLs, unknown tags — is dropped. Forbidden tags keep
 * their (cleaned) text children so no content silently disappears.
 *
 * Runs in the Capacitor/browser webview; there is no SSR/Node path here.
 */

import { API_BASE_URL } from '@/utils/env';

/** Inline + block formatting tags safe to render. */
const ALLOWED_TAGS = new Set<string>([
  'p', 'br', 'hr', 'span', 'div',
  'strong', 'b', 'em', 'i', 'u', 's', 'small', 'mark', 'sub', 'sup',
  'a', 'img',
  'ul', 'ol', 'li',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
  'blockquote', 'code', 'pre',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
]);

/** Tags whose entire contents (incl. text) must be discarded outright. */
const DROP_WITH_CONTENT = new Set<string>(['script', 'style', 'iframe', 'object', 'embed']);

/** Per-tag attribute allowlist. Tags absent here keep no attributes. */
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'title']),
  img: new Set(['src', 'alt', 'title']),
};

/** URL schemes permitted as-is on href/src. */
const SAFE_URL = /^(https?:|mailto:|tel:)/i;

/** Any explicit scheme (`javascript:`, `data:`, `file:`…). */
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Resolve a link/image URL, or return '' to drop the attribute.
 *
 * ERPat stores uploaded files at its web ROOT and embeds them in rich text as
 * ROOT-RELATIVE paths (`uploads/notes/receipt.png`), the same shape avatars come
 * back in. Dropping every relative URL therefore stripped the `src`/`href` off
 * anything a user attached in ERPat, so the file could not be opened at all.
 * Relative paths now resolve against the API origin instead — matching how
 * `profileImageUrl` resolves avatars.
 *
 * Still dropped: any explicit non-safe scheme, and protocol-relative
 * (`//host/x`) URLs, which point at an arbitrary host rather than ERPat. The
 * one exception is a base64 RASTER image on an `<img>`, which is how a sketch
 * drawn in the app is carried inside a note body — SVG stays excluded because
 * it can execute script.
 */
/**
 * Rebuild a data URI whose `data:` scheme was stripped off.
 *
 * ERPat's web editor mangles an embedded sketch when it re-saves the note: the
 * `<img>` survives but its `src` loses the scheme, leaving a bare base64
 * payload (or `image/png;base64,…`). The browser then reads that as a relative
 * URL and shows a broken image — in BOTH apps, since it is one shared record.
 *
 * Only unmistakable image payloads are repaired: a long base64 body that either
 * declares its own `image/<type>;base64,` prefix or opens with a known file
 * signature. Anything shorter or ambiguous is left alone and dropped as before,
 * so this can't turn arbitrary text into a URL.
 */
function repairImageDataUrl(value: string): string {
  const compact = value.replace(/\s+/g, '');

  // Scheme stripped, MIME kept: "image/png;base64,iVBOR..."
  const withMime = /^image\/(png|jpe?g|gif|webp);base64,([a-z0-9+/=]{200,})$/i.exec(compact);
  if (withMime) return `data:${compact}`;

  // Scheme and MIME both gone: identify by the base64 file signature.
  if (!/^[a-z0-9+/=]{200,}$/i.test(compact)) return '';
  const SIGNATURES: ReadonlyArray<[string, string]> = [
    ['iVBORw0KGgo', 'png'],
    ['/9j/', 'jpeg'],
    ['R0lGOD', 'gif'],
    ['UklGR', 'webp'],
  ];
  for (const [prefix, type] of SIGNATURES) {
    if (compact.startsWith(prefix)) return `data:image/${type};base64,${compact}`;
  }
  return '';
}

function resolveUrl(raw: string, tag = ''): string {
  const value = raw.trim();
  if (!value) return '';
  if (SAFE_URL.test(value)) return value;
  // An inline raster image — how a drawn sketch travels inside a note body.
  // Images only, and never SVG: an SVG data URI can carry script, a PNG cannot.
  if (tag === 'img') {
    if (/^data:image\/(png|jpe?g|gif|webp);base64,[a-z0-9+/=\s]+$/i.test(value)) return value;
    const repaired = repairImageDataUrl(value);
    if (repaired) return repaired;
  }
  if (value.startsWith('//')) return '';
  if (HAS_SCHEME.test(value)) return '';
  // In dev API_BASE_URL is the Vite origin, whose proxy forwards /uploads and
  // /files to the backend — so this resolves correctly in both environments.
  try {
    const origin = new URL(API_BASE_URL).origin;
    return new URL(value.startsWith('/') ? value : `/${value}`, origin).toString();
  } catch {
    return '';
  }
}

function cleanInto(source: Node, target: Node, doc: Document): void {
  source.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      target.appendChild(doc.createTextNode(child.textContent ?? ''));
      return;
    }
    // Drop comments, processing instructions, etc.
    if (child.nodeType !== Node.ELEMENT_NODE) return;

    const el = child as Element;
    const tag = el.tagName.toLowerCase();

    if (DROP_WITH_CONTENT.has(tag)) return;

    // Unknown tag: unwrap — keep its cleaned children, drop the element itself.
    if (!ALLOWED_TAGS.has(tag)) {
      cleanInto(el, target, doc);
      return;
    }

    const safe = doc.createElement(tag);
    const allowed = ALLOWED_ATTRS[tag];
    if (allowed) {
      for (const attr of Array.from(el.attributes)) {
        const name = attr.name.toLowerCase();
        if (!allowed.has(name)) continue;
        if (name === 'href' || name === 'src') {
          const url = resolveUrl(attr.value, tag);
          if (!url) continue;
          safe.setAttribute(name, url);
          continue;
        }
        safe.setAttribute(name, attr.value);
      }
    }
    // Harden links: open externally, never leak the opener or referrer.
    if (tag === 'a') {
      safe.setAttribute('target', '_blank');
      safe.setAttribute('rel', 'noopener noreferrer nofollow');
    }

    cleanInto(el, safe, doc);
    target.appendChild(safe);
  });
}

/**
 * Returns sanitized HTML safe to bind with `v-html`.
 * Empty/blank input yields an empty string.
 */
export function sanitizeHtml(dirty: string | null | undefined): string {
  if (!dirty) return '';
  const doc = new DOMParser().parseFromString(dirty, 'text/html');
  const out = doc.createElement('div');
  cleanInto(doc.body, out, doc);
  return out.innerHTML;
}

/** Escape the HTML-significant characters so text renders literally (never as markup). */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Plain multi-line text (a small, non-rich description field) into the same
 * `<p>`-per-paragraph shape every other note body already uses — so a
 * Scribble's description round-trips through the same rendering path
 * (`renderMessageBody`, `htmlToText`, ERPat's own web module) as rich-text
 * note bodies, rather than needing a plain-text special case throughout.
 * Blank lines are dropped rather than kept as empty paragraphs.
 */
export function textToParagraphsHtml(text: string): string {
  return text
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join('');
}

/** Matches at least one real HTML element tag (`<p>`, `<a href=…>`, `</li>`). */
const HTML_TAG_RE = /<([a-z][a-z0-9]*)\b[^>]*>|<\/[a-z][a-z0-9]*\s*>/i;

/**
 * Escape plain text, then auto-link http(s) URLs + emails and turn newlines into
 * `<br>`. XSS-safe: the text is escaped FIRST, so every link target is a
 * substring of already-escaped text (no `"`/`<`/`>` can break out of the
 * attribute) and the only schemes emitted are `http(s):` / `mailto:`.
 */
function linkifyPlainText(text: string): string {
  return escapeHtml(text)
    .replace(
      /(https?:\/\/[^\s<]+[^\s<.,;:!?)])/g,
      (m) => `<a href="${m}" target="_blank" rel="noopener noreferrer nofollow">${m}</a>`,
    )
    .replace(
      /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
      (m) => `<a href="mailto:${m}">${m}</a>`,
    )
    .replace(/\r\n|\r|\n/g, '<br>');
}

/**
 * Render a chat/message body as HTML safe to bind with `v-html`.
 *
 * Message bodies are mixed-format: rich text composed in the web app arrives as
 * HTML, while text typed in the mobile composer is plain (and may be multi-line).
 * So we branch:
 *  - HTML-looking input → {@link sanitizeHtml} (formatting kept; scripts,
 *    handlers and unsafe URLs stripped) — the same path advisories/timeline use.
 *  - Plain text → escaped + auto-linked + newlines as `<br>`, so line breaks
 *    survive (the old `white-space: pre-wrap` behaviour) and the markup is never
 *    interpreted.
 */
export function renderMessageBody(raw: string | null | undefined): string {
  if (!raw) return '';
  return HTML_TAG_RE.test(raw) ? sanitizeHtml(raw) : linkifyPlainText(raw);
}

/**
 * Flattens HTML to a single-line plain-text excerpt — for list previews where
 * markup would otherwise render as raw tags. Block-level boundaries and <br>
 * collapse to spaces so paragraphs don't run together.
 */
export function htmlToText(html: string | null | undefined): string {
  if (!html) return '';
  const withBreaks = html
    .replace(/<\/(p|div|li|h[1-6]|blockquote|tr)>/gi, '$& ')
    .replace(/<br\s*\/?>/gi, ' ');
  const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/** A file referenced by a note/advisory body — an image `src` or a link `href`. */
export interface EmbeddedFile {
  /** Absolute, already-sanitized URL. */
  url: string;
  /** File name from the URL path, e.g. "receipt.png". */
  name: string;
  /** Lower-case extension without the dot ('' when the path has none). */
  ext: string;
  /** True when the browser can usually render it inline. */
  viewable: boolean;
}

/** Extensions a webview renders inline; everything else is download-only. */
const INLINE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'bmp', 'svg', 'pdf']);

/**
 * Percent-decode a path segment, falling back to it verbatim.
 *
 * `new URL()` accepts a malformed escape that `decodeURIComponent` rejects
 * ("https://host/%E0%A4%A.pdf" parses, then throws on decode). This runs while
 * the detail modal renders, so one such link used to take the whole note view
 * down — an undecoded file name is a far better outcome than a blank note.
 */
function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Collect the files a SANITIZED body references, so a host can list them even
 * when the inline render fails — a PDF or an office document has nothing to
 * show in place, and a broken <img> shows only a placeholder.
 *
 * Must be given already-sanitized HTML: the URLs are then absolute and every
 * unsafe scheme has been dropped, so nothing here needs to re-check them.
 */
export function extractEmbeddedFiles(safeHtml: string | null | undefined): EmbeddedFile[] {
  if (!safeHtml) return [];

  const doc = new DOMParser().parseFromString(safeHtml, 'text/html');
  const seen = new Set<string>();
  const files: EmbeddedFile[] = [];

  const add = (raw: string | null): void => {
    if (!raw || seen.has(raw)) return;
    let path: string;
    try {
      path = new URL(raw).pathname;
    } catch {
      return;
    }
    const name = safeDecode(path.split('/').filter(Boolean).pop() ?? '');
    // A URL with no file name (a page link, not a file) isn't an attachment.
    if (!name || !name.includes('.')) return;

    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    seen.add(raw);
    files.push({ url: raw, name, ext, viewable: INLINE_EXT.has(ext) });
  };

  doc.querySelectorAll('img[src]').forEach((el) => add(el.getAttribute('src')));
  doc.querySelectorAll('a[href]').forEach((el) => add(el.getAttribute('href')));

  return files;
}
